import React, { useState, useEffect } from 'react';
import { FaExclamationTriangle, FaExclamationCircle, FaCheckCircle } from 'react-icons/fa';
import './Warnings.css';
import useStatusAnimation from '../../hooks/useStatusAnimation';
import { generateAssetData, ASSETS, STATUS_TYPES } from '../../utils/dataGenerator';

const Warnings = () => {
  const { currentStatus } = useStatusAnimation(true);
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    const assetData = generateAssetData(currentStatus);
    const allWarnings = [];

    Object.values(assetData).forEach(asset => {
      asset.messages.forEach(msg => {
        allWarnings.push({
          id: Math.random().toString(36).substr(2, 9),
          asset: asset.name,
          message: msg.text,
          prediction: msg.prediction,
          status: asset.status,
          timestamp: new Date().toISOString(),
          severity: getSeverity(asset.status)
        });
      });
    });

    setWarnings(allWarnings);
  }, [currentStatus]);

  const getSeverity = (status) => {
    switch (status) {
      case STATUS_TYPES.CRITICAL:
        return 'critical';
      case STATUS_TYPES.WARNING:
        return 'warning';
      default:
        return 'info';
    }
  };

  const getIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <FaExclamationTriangle />;
      case 'warning':
        return <FaExclamationCircle />;
      default:
        return <FaCheckCircle />;
    }
  };

  const getColor = (severity) => {
    switch (severity) {
      case 'critical':
        return '#FF4444';
      case 'warning':
        return '#FF8800';
      default:
        return '#00C851';
    }
  };

  return (
    <div className="warnings-page">
      <div className="page-header">
        <h1>Warnungen & Meldungen</h1>
        <p>Übersicht aller Systemwarnungen und Wartungsempfehlungen</p>
      </div>

      <div className="warnings-stats">
        <div className="stat-card critical">
          <div className="stat-value">{warnings.filter(w => w.severity === 'critical').length}</div>
          <div className="stat-label">Kritisch</div>
        </div>
        <div className="stat-card warning">
          <div className="stat-value">{warnings.filter(w => w.severity === 'warning').length}</div>
          <div className="stat-label">Warnungen</div>
        </div>
        <div className="stat-card info">
          <div className="stat-value">{warnings.filter(w => w.severity === 'info').length}</div>
          <div className="stat-label">Informationen</div>
        </div>
      </div>

      <div className="warnings-list">
        <h2>Aktuelle Meldungen</h2>
        {warnings.length === 0 ? (
          <div className="no-warnings">
            <FaCheckCircle />
            <p>Keine Warnungen vorhanden</p>
          </div>
        ) : (
          warnings.map(warning => (
            <div 
              key={warning.id} 
              className={`warning-item ${warning.severity}`}
              style={{ borderLeftColor: getColor(warning.severity) }}
            >
              <div className="warning-icon" style={{ color: getColor(warning.severity) }}>
                {getIcon(warning.severity)}
              </div>
              <div className="warning-content">
                <div className="warning-header">
                  <h3>{warning.asset}</h3>
                  <span className="warning-time">
                    {new Date(warning.timestamp).toLocaleTimeString('de-DE')}
                  </span>
                </div>
                <p className="warning-message">{warning.message}</p>
                {warning.prediction && (
                  <div className="warning-prediction">
                    <span className="prediction-label">Ausfall berechnet am:</span>
                    <span className="prediction-date">
                      {new Date(warning.prediction.date).toLocaleDateString('de-DE', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric' 
                      })}
                    </span>
                    <span className="prediction-probability" style={{ 
                      color: warning.prediction.probability >= 80 ? '#FF4444' : '#FF8800' 
                    }}>
                      {warning.prediction.probability}% Wahrscheinlichkeit
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Warnings;
